import React from 'react';
import {
  Paper,
  IconButton,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import CryptoLatin1Encoder from 'crypto-js/enc-latin1';
import { CloseOutlined } from '@mui/icons-material';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import translations from '../../assets/translations';
import { Trans } from 'react-i18next';
import { DeviceService } from '../../services';
import i18next from 'i18next';
import { BaseModalStyles } from './common/modalUtils';
import { getTheme } from '../../common/theme';
import CryptoJS from 'crypto-js';
import { SnackbarUtils } from '../StyledSnackbarProvider';
import { store } from '../../store';

type Props = WithStyles<any> & {
  closeModal: () => void;
};

type State = {
  loading: boolean;
  activeStep: number;
  checkResult?: any;
  importFaliFlag: boolean;
  confirmContent?: string | React.ReactElement;
  csvKey?: string;
  file?: {
    data: any;
    md5: string;
  };
};

const styles = () => {
  const theme = getTheme();
  return {
    ...BaseModalStyles(),
    stepper: {
      padding: 0,
    },
    fileInput: {
      display: 'none',
    },
    warning: {
      color: theme.palette.error.main,
      textAlign: 'center' as const,
    },
  };
};

class ImportDeviceListMC extends React.Component<Props, State> {
  constructor(props: Props, state: State) {
    super(props, state);
    this.fileUploadRef = React.createRef<HTMLInputElement>();
  }
  store = store.getState();
  state: State = {
    activeStep: 0,
    loading: false,
    checkResult: {},
    confirmContent: "",
    importFaliFlag: false,
    csvKey: "",
    file: undefined,
  }
  fileUploadRef: React.RefObject<HTMLInputElement>;
  deviceService!: any;
  maxSteps = translations.en.translation.modals.importDeviceList.activePageHeader.length;

  componentDidMount = async (): Promise<void> => {
    this.deviceService = await DeviceService.create();
  };

  get isValid(): boolean {
    switch (this.state.activeStep) {
      case 0:
        return (
          !this.state.loading &&
          !!this.state.file
        );
      case 1:
        return (
          !this.state.loading
        );
      default:
        return false;
    }
  }

  importDeviceList = async (): Promise<void> => {
    this.setState({ loading: true });
    if (this.state.file !== undefined) {
      if (this.state.file!.data.type !== "text/csv") {
        SnackbarUtils.error(i18next.t('error.attach.fileType'));
        this.setState({ loading: false, file: undefined });
        this.fileUploadRef.current!.value = "";
        return;
      }
      try {
        const prepareData = await this.deviceService.device.prepare();
        await this.deviceService.authenticationServiceProvider
        .uploadFile({
          Bucket: prepareData.data.bucket,
          Key: prepareData.data.key,
          Body: this.state.file!.data,
          Metadata: { token: prepareData.data.token },
        }).promise();
        try {
          const csvKey = prepareData.data.key.split("/")[2];
          const checkResult = await this.deviceService.device.checkContractCsv({csvKey});
          this.setState(
            {
              activeStep: 1,
              checkResult: checkResult.data,
              loading: false,
              confirmContent: (
                <Grid item xs={12}>
                  {checkResult.data.stateChanges?.Active && checkResult.data.totalChanges > 0 && (
                    <p>{checkResult.data.stateChanges.Active} devices change to Active</p>
                  )}
                  {checkResult.data.stateChanges && checkResult.data.stateChanges["Pause"] && checkResult.data.totalChanges > 0 && (
                    <p>{checkResult.data.stateChanges["Pause"]} devices change to Pause</p>
                  )}
                  {checkResult.data.stateChanges && checkResult.data.stateChanges["Standby to Pause"] && checkResult.data.totalChanges > 0 && (
                    <p>{checkResult.data.stateChanges["Standby to Pause"]} devices change to Standby to pause</p>
                  )}
                  {checkResult.data.stateChanges && checkResult.data.stateChanges["Terminate"] && checkResult.data.totalChanges > 0 && (
                    <p>{checkResult.data.stateChanges["Terminate"]} devices change to Standby to terminate</p>
                  )}
                  {checkResult.data.totalChanges === 0 && (
                    <p>No change found.</p>
                  )}
                </Grid>
              ),
              csvKey,
            }
          );
          if (checkResult.data.totalChanges > 0) {
            SnackbarUtils.success(i18next.t('success.attach.deviceImportDeviceList'));
          }
        } catch (e) {
          this.setState({ loading: false, importFaliFlag: true });
          const checkError: any = e;
          SnackbarUtils.error(
            (checkError.response &&
              checkError.response.data &&
              checkError.response.data.message) ||
            i18next.t('error.attach.deviceImportDeviceList'),
          );
        }
      } catch (e) {
        this.setState({ loading: false, importFaliFlag: true });
        const error: any = e;
        SnackbarUtils.error(
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          i18next.t('error.tryAgain'),
        );
      }
    }
  }

  confirmImportData = async (): Promise<void> => {
    this.setState({ loading: true });
    try {
      await this.deviceService.device.updateContractByCsv(this.state.csvKey);
      this.setState({
        activeStep: 2,
        loading: false
      })
      if (this.store.modalStore.data && this.store.modalStore.data.callback) this.store.modalStore.data.callback();
      SnackbarUtils.success(i18next.t('success.update.deviceImportDeviceList'));
    } catch (e) {
      this.setState({ loading: false });
      const updateError: any = e;
      SnackbarUtils.error(
        (updateError.response &&
          updateError.response.data &&
          updateError.response.data.message) ||
        i18next.t('error.update.deviceImportDeviceList'),
      );
    }
  }

  handleFileSelect = (e: any): void => {
    e.preventDefault();
    const data = e.target.files[0];
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const binary = CryptoLatin1Encoder.parse(e.target!.result);
      this.setState({ file: { data, md5: CryptoJS.MD5(binary).toString() } });
    };
    reader.readAsBinaryString(data);
    this.setState({ importFaliFlag: false });
  };

  render = (): React.ReactNode => {
    return (
      <Paper tabIndex={-1} square className={this.props.classes.paper}>
        <IconButton
          style={{ ...BaseModalStyles().closeButton }}
          onClick={this.props.closeModal}
          aria-label="close"
          size="large"
        >
          <CloseOutlined />
        </IconButton>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography variant="h3">
              {translations.en.translation.modals.importDeviceList.title}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1">
              {translations.en.translation.modals.importDeviceList.description}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Stepper alternativeLabel activeStep={this.state.activeStep} className={this.props.classes.stepper}>
              {translations.en.translation.modals.importDeviceList.activePageHeader.map((step) => (
                <Step key={step}>
                  <StepLabel>{step}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>
          {this.state.activeStep === 0 && this.state.importFaliFlag && (
            <Grid item xs={12}>
              <Typography className={this.props.classes.warning}>
                {i18next.t('description.deviceImportCsvFail')}
              </Typography>
            </Grid>
          )}
          {this.state.activeStep === 0 && (
            <React.Fragment>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => this.fileUploadRef.current!.click()}
                >
                  <input
                    id="file-upload"
                    ref={this.fileUploadRef}
                    type="file"
                    accept=".csv"
                    className={this.props.classes.fileInput}
                    onChange={this.handleFileSelect}
                  />
                  <Grid container justifyContent="center">
                    <Grid item xs={12}>
                      <Trans>
                        {this.state.file ? 'form.changeFile' : 'form.selectFile'}
                      </Trans>
                    </Grid>
                    <Grid item xs={12}>
                      <pre style={{ display: 'inline-block', margin: 0 }}>
                        {this.state.file
                          ? this.state.file.data.name
                          : i18next.t('description.noFileSelected')}
                      </pre>
                    </Grid>
                  </Grid>
                </Button>
              </Grid>
            </React.Fragment>
          )}
          {this.state.activeStep === 1 && (
            <Grid item xs={12}>
              {this.state.confirmContent}
            </Grid>
          )}
          {this.state.activeStep === 2 && (
            <Grid item xs={12}>
              <Trans>description.import.completed</Trans>
            </Grid>
          )}
          <Grid item>
            {this.state.activeStep === 0 && (
              <Button color="secondary" variant="contained" disabled={!this.isValid} onClick={this.importDeviceList}>
                {this.state.loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <Trans>action.uploadFile</Trans>
                )}
              </Button>
            )}
            {this.state.activeStep === 1 && this.state.checkResult?.totalChanges > 0 && (
              <Button color="secondary" variant="contained" disabled={!this.isValid} onClick={this.confirmImportData}>
                {this.state.loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <Trans>action.confirm</Trans>
                )}
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>
    )
  }
}

export default withStyles(styles)(ImportDeviceListMC as any);
